import type { Universe } from '../astro/Universe';

export type LogEntry = { id: string; label: string };

/** Exploration goals, in suggested order. Saved per browser. */
export const LOG_ENTRIES: LogEntry[] = [
  { id: 'land:earth', label: 'Land on Earth' },
  { id: 'visit:moon', label: 'Reach the Moon' },
  { id: 'visit:venus', label: 'Visit Venus' },
  { id: 'visit:mars', label: 'Visit Mars' },
  { id: 'visit:mercury', label: 'Visit Mercury' },
  { id: 'visit:sun', label: 'Approach the Sun' },
  { id: 'belt', label: 'Cross the asteroid belt' },
  { id: 'visit:ceres', label: 'Visit Ceres' },
  { id: 'visit:jupiter', label: 'Visit Jupiter' },
  { id: 'visit:io', label: 'Visit Io' },
  { id: 'visit:europa', label: 'Visit Europa' },
  { id: 'visit:ganymede', label: 'Visit Ganymede' },
  { id: 'visit:callisto', label: 'Visit Callisto' },
  { id: 'visit:saturn', label: 'Visit Saturn' },
  { id: 'visit:titan', label: 'Visit Titan' },
  { id: 'visit:uranus', label: 'Visit Uranus' },
  { id: 'visit:neptune', label: 'Visit Neptune' },
  { id: 'visit:pluto', label: 'Visit Pluto' },
];

type Saved = { done: string[]; points: number };

export class Logbook {
  private readonly done = new Set<string>();
  points = 0;
  onNew: (entry: LogEntry) => void = () => {};

  constructor() {
    try {
      const raw = localStorage.getItem('spaceship.logbook');
      if (raw) {
        const s: Saved = JSON.parse(raw);
        s.done.forEach((d) => this.done.add(d));
        this.points = s.points ?? 0;
      }
    } catch {
      /* storage unavailable: start fresh */
    }
  }

  get count(): number {
    return this.done.size;
  }

  get total(): number {
    return LOG_ENTRIES.length;
  }

  has(id: string): boolean {
    return this.done.has(id);
  }

  next(): LogEntry | null {
    return LOG_ENTRIES.find((e) => !this.done.has(e.id)) ?? null;
  }

  mark(id: string): void {
    if (this.done.has(id)) return;
    const entry = LOG_ENTRIES.find((e) => e.id === id);
    if (!entry) return;
    this.done.add(id);
    this.save();
    this.onNew(entry);
  }

  setPoints(p: number): void {
    this.points = p;
    this.save();
  }

  /** Visit = within 5 radii of a body (20 for the Sun). */
  check(universe: Universe, pos: import('three').Vector3, inBelt: boolean): void {
    for (const b of universe.bodies) {
      const id = `visit:${b.def.id}`;
      if (this.done.has(id)) continue;
      const k = b.def.kind === 'star' ? 20 : 5;
      if (b.position.distanceTo(pos) < b.def.radius * k) this.mark(id);
    }
    if (inBelt) this.mark('belt');
  }

  private save(): void {
    try {
      localStorage.setItem('spaceship.logbook', JSON.stringify({ done: [...this.done], points: this.points } satisfies Saved));
    } catch {
      /* ignore */
    }
  }
}
